/**
 * "Raise prices" — the first section of the Bulk price changes page (gap 1 PR 2, DEC-383, D-13).
 *
 * Pick a job type, the customers, +$ or +% (a minus is a cut), and a start date (today or
 * later); preview old -> new per customer; save. Unticked, the checkbox moves only existing
 * special prices; ticked, a customer without one gets one: the price in force (customer rate or
 * standard) plus the raise. A day that already has a change says so in the preview (D-08).
 *
 * Built from the shared bulk components (gap1-13). The preview's request and token are what is
 * saved (usePreviewApply); changing an input drops the preview. The API is the gate
 * (MANAGE_CUSTOMER_PRICES); nothing here makes a trust decision.
 */

import React from 'react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import type { BulkSectionProps } from '../../routes/BulkPriceChangesPage';
import { AmountChangeInput, readAmountChange, type AmountDraft } from './AmountChangeInput';
import { PriceChangePreview, type PreviewColumn } from './PriceChangePreview';
import { StartDateInput } from './StartDateInput';
import { usePreviewApply } from './use-preview-apply';
import {
  formatDay,
  priceText,
  type JobTypeBulkApplyResponse,
  type JobTypeBulkRequest,
  type JobTypePreviewRow,
} from './customer-prices';
import {
  BULK_APPLY_PATH,
  BULK_PREVIEW_PATH,
  CUSTOMER_PRICES_KEY,
  useJobTypeCatalog,
} from './customer-prices-hooks';
import { useFeeCustomers } from './customer-disposal-fees-hooks';

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  marginBottom: '4px',
};
const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };
const errorStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-danger)' };
const selectStyle: React.CSSProperties = {
  minHeight: '38px',
  padding: '8px 10px',
  fontSize: '14px',
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  color: 'var(--color-text-primary)',
};
const listStyle: React.CSSProperties = {
  maxHeight: '220px',
  overflow: 'auto',
  border: '1px solid var(--color-border-secondary)',
  borderRadius: '10px',
  padding: '6px 10px',
  margin: '6px 0',
};

type Row = JobTypePreviewRow;

function cents(row: Row, value: number | null): string {
  if (value === null) return 'No price';
  return priceText(value, row.jobType?.billingMethod ?? '');
}

const COLUMNS: readonly PreviewColumn<Row>[] = [
  { key: 'customer', header: 'Customer', cell: (r) => r.customer?.name ?? '—' },
  { key: 'jobType', header: 'Job type', cell: (r) => r.jobType?.label ?? '—' },
  { key: 'now', header: 'Now', cell: (r) => cents(r, r.currentCents) },
  { key: 'new', header: 'New', cell: (r) => (r.newCents === null ? '—' : cents(r, r.newCents)) },
  { key: 'from', header: 'From', cell: (r) => formatDay(r.effectiveFrom) },
];

function customerText(c: { name: string | null; abbreviation: string }): string {
  return c.name === null ? c.abbreviation : `${c.abbreviation} — ${c.name}`;
}

function savedText(n: number): string {
  return `Saved ${String(n)} ${n === 1 ? 'price' : 'prices'}.`;
}

export function RaisePricesSection(props: BulkSectionProps): React.JSX.Element {
  const catalog = useJobTypeCatalog();
  const customers = useFeeCustomers();
  const bulk = usePreviewApply<JobTypeBulkRequest, Row, JobTypeBulkApplyResponse>({
    previewPath: BULK_PREVIEW_PATH,
    applyPath: BULK_APPLY_PATH,
    invalidate: [CUSTOMER_PRICES_KEY],
  });
  const [familyId, setFamilyId] = React.useState('');
  const [chosen, setChosen] = React.useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = React.useState('');
  const [amount, setAmount] = React.useState<AmountDraft>({ mode: 'PERCENT', raw: '' });
  const [effectiveFrom, setEffectiveFrom] = React.useState(props.today);
  const [includeUnpriced, setIncludeUnpriced] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [amountError, setAmountError] = React.useState<string | null>(null);

  const all = customers.data?.customers ?? [];
  const needle = search.trim().toLowerCase();
  const shown =
    needle === ''
      ? all
      : all.filter(
          (c) =>
            c.abbreviation.toLowerCase().includes(needle) ||
            (c.name ?? '').toLowerCase().includes(needle),
        );

  /** Any input change drops the preview: what is saved is always what is shown. */
  function edit(fn: () => void): void {
    fn();
    setProblem(null);
    bulk.reset();
  }

  /** The request for the current inputs, or why it can't be previewed yet. */
  function buildRequest(unpriced: boolean): JobTypeBulkRequest | null {
    if (familyId === '') {
      setProblem('Pick a job type.');
      return null;
    }
    if (chosen.size === 0) {
      setProblem('Pick at least one customer.');
      return null;
    }
    const read = readAmountChange(amount.mode, amount.raw);
    if (!read.ok) {
      setAmountError(read.error);
      return null;
    }
    if (effectiveFrom === '' || effectiveFrom < props.today) {
      setProblem('Start dates can be today or later.');
      return null;
    }
    setProblem(null);
    setAmountError(null);
    return {
      kind: 'RAISE',
      familyId,
      customerIds: all.filter((c) => chosen.has(c.id)).map((c) => c.id),
      change: read.change,
      includeUnpriced: unpriced,
      effectiveFrom,
    };
  }

  function runPreview(unpriced: boolean): void {
    const req = buildRequest(unpriced);
    if (req !== null) void bulk.preview(req);
  }

  const rows = bulk.rows;
  const toSave =
    rows === null ? 0 : rows.filter((r) => r.status === 'ADD' || r.status === 'CHANGE').length;
  const busy = bulk.status === 'previewing' || bulk.status === 'applying';

  if (catalog.isError) {
    return (
      <p role="alert" style={errorStyle}>
        Could not load the job types. Reload the page to try again.
      </p>
    );
  }
  const jobTypes = catalog.data?.jobTypes ?? [];

  return (
    <div style={{ display: 'grid', gap: '14px', maxWidth: '900px' }}>
      <div>
        <label htmlFor="raise-job-type" style={labelStyle}>
          Job type
        </label>
        <select
          id="raise-job-type"
          value={familyId}
          style={selectStyle}
          onChange={(e): void => edit(() => setFamilyId(e.target.value))}
        >
          <option value="">Choose a job type…</option>
          {jobTypes.map((j) => (
            <option key={j.familyId} value={j.familyId}>
              {j.label}
            </option>
          ))}
        </select>
      </div>

      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={labelStyle}>Customers</legend>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <Input
            aria-label="Search customers"
            placeholder="Search customers"
            value={search}
            onChange={(e): void => setSearch(e.target.value)}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={(): void =>
              edit(() => setChosen(new Set([...chosen, ...shown.map((c) => c.id)])))
            }
          >
            Select all shown
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={chosen.size === 0}
            onClick={(): void => edit(() => setChosen(new Set()))}
          >
            Clear
          </Button>
        </div>
        <div style={listStyle}>
          {customers.isLoading && <p style={helpStyle}>Loading customers…</p>}
          {customers.isError && (
            <p role="alert" style={errorStyle}>
              Could not load the customers. Reload the page to try again.
            </p>
          )}
          {shown.map((c) => (
            <label key={c.id} style={{ display: 'flex', gap: '8px', padding: '2px 0' }}>
              <input
                type="checkbox"
                checked={chosen.has(c.id)}
                onChange={(e): void => {
                  const on = e.target.checked;
                  edit(() => {
                    const next = new Set(chosen);
                    if (on) next.add(c.id);
                    else next.delete(c.id);
                    setChosen(next);
                  });
                }}
              />
              {customerText(c)}
            </label>
          ))}
          {customers.data !== undefined && shown.length === 0 && (
            <p style={helpStyle}>No customers match.</p>
          )}
        </div>
        <p style={helpStyle}>{`${String(chosen.size)} selected`}</p>
      </fieldset>

      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <AmountChangeInput
          value={amount}
          error={amountError}
          onChange={(v): void =>
            edit(() => {
              setAmount(v);
              setAmountError(null);
            })
          }
        />
        <StartDateInput
          value={effectiveFrom}
          today={props.today}
          onChange={(v): void => edit(() => setEffectiveFrom(v))}
        />
      </div>

      <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={includeUnpriced}
          onChange={(e): void => {
            const on = e.target.checked;
            setIncludeUnpriced(on);
            // A preview that is up is redone with the new choice.
            if (bulk.rows !== null || bulk.status === 'stale' || bulk.status === 'error') {
              runPreview(on);
            }
          }}
        />
        Also give customers without a special price one
      </label>
      <p style={{ ...helpStyle, marginTop: '-8px' }}>
        Ticked, a customer without a special price gets one: today&apos;s price for them (customer
        rate or standard) plus this change. Unticked, only existing special prices move.
      </p>

      {problem !== null && (
        <p role="alert" style={errorStyle}>
          {problem}
        </p>
      )}
      <div>
        <Button type="button" disabled={busy} onClick={(): void => runPreview(includeUnpriced)}>
          Preview
        </Button>
      </div>

      {bulk.status === 'previewing' && (
        <p role="status" style={helpStyle}>
          Working out the new prices…
        </p>
      )}
      {bulk.status === 'error' && bulk.error !== null && (
        <p role="alert" style={errorStyle}>
          {bulk.error}
        </p>
      )}
      {bulk.status === 'stale' && (
        <div role="alert" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span style={errorStyle}>{bulk.error}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={(): void => runPreview(includeUnpriced)}
          >
            Preview again
          </Button>
        </div>
      )}
      {bulk.status === 'done' && bulk.result !== null && (
        <p role="status" style={{ color: 'var(--color-success)' }}>
          {savedText(bulk.result.applied.add + bulk.result.applied.change)}
        </p>
      )}

      {rows !== null && bulk.status !== 'done' && (
        <>
          {rows.length === 0 ? (
            <p style={helpStyle}>Nothing to change for these customers.</p>
          ) : (
            <PriceChangePreview rows={rows} columns={COLUMNS} />
          )}
          <div>
            <Button
              type="button"
              disabled={!bulk.canApply || toSave === 0}
              onClick={(): void => {
                void bulk.apply();
              }}
            >
              {`Save ${String(toSave)} ${toSave === 1 ? 'change' : 'changes'}`}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
